import { CollectionStatusEnum } from "@kanpla/api-contract";
import { useMemo } from "react";

import { useCollectStudents } from "~/api/children/hooks/use-collect-students";
import { CollectNameFilter } from "~/components/collect/operator/collect-name-filter";
import { useSelectorFilter } from "~/components/collect/operator/use-selector-filter";
import { collectActions } from "~/providers/store/collect/actions";
import {
  useCollectSearchBy,
  useCollectSelectedLetter,
  useCollectStatusFilter
} from "~/providers/store/collect/hooks";
import { PaginationContextProvider } from "~/components/pagination/context";
import { useCurrentSchool } from "~/hooks/queries/schools";
import { getCountsByStatus } from "~/utils/collect/get-counts-by-status";

import { DiscoveryHeader } from "./discovery-header";
import { DiscoveryMain } from "./discovery-main";
import { getDiscoveryView } from "./discovery-view";
import { Footer, FooterPagination } from "./footer";
import { GroupFilterRegion } from "./group-filter-region";
import { Layout } from "./layout";
import { filterByInitial, getAvailableInitials } from "./name-filter";
import { useAlphabet } from "./use-alphabet";
import { useFittingRows } from "./use-fitting-rows";

// Single source for the grid width: sets the page size and the StudentsGrid columns.
const GRID_COLUMNS = 3;
// Approx UserCard row height (incl. 1px gap), for fitting rows to the screen.
const STUDENT_ROW_HEIGHT = 72;

export const CollectOperatorPage = () => {
  const { data, status, refetch, isRefetching } = useCollectStudents();
  const {
    data: school,
    status: schoolStatus,
    refetch: refetchSchool,
    isRefetching: isRefetchingSchool
  } = useCurrentSchool();

  const rows = useFittingRows(STUDENT_ROW_HEIGHT);
  const studentsPerPage = rows * GRID_COLUMNS;

  // Status is the primary filter; group and name-initials narrow within it.
  const statusFilter = useCollectStatusFilter();
  const alphabet = useAlphabet();
  const searchBy = useCollectSearchBy();
  const selectedLetter = useCollectSelectedLetter();

  const students = useMemo(() => data ?? [], [data]);

  const studentsByStatus = useMemo(
    () =>
      statusFilter === "all"
        ? students
        : students.filter(
            (student) =>
              (student.collectionStatus ?? CollectionStatusEnum.NO_ORDERS) ===
              statusFilter
          ),
    [students, statusFilter]
  );

  // Year/class filter from the server taxonomy; layer names are the headings.
  const {
    layers,
    isActive: hasGroupFilter,
    selectionKey: groupSelectionKey,
    hasSelection: hasGroupSelection,
    reset: resetGroupFilter,
    filter: filterByGroup
  } = useSelectorFilter({
    layers: school?.layers ?? [],
    firstLayerOptions: school?.firstLayerOptions ?? [],
    optionsByFirstLayer: school?.optionsByFirstLayer ?? {}
  });

  // Apply group to the status-filtered set; name-initials narrows that.
  const scopedStudents = useMemo(
    () => filterByGroup(studentsByStatus),
    [filterByGroup, studentsByStatus]
  );
  const availableLetters = useMemo(
    () => getAvailableInitials(scopedStudents, searchBy),
    [scopedStudents, searchBy]
  );
  const filteredStudents = useMemo(
    () => filterByInitial(scopedStudents, searchBy, selectedLetter),
    [scopedStudents, searchBy, selectedLetter]
  );

  const countsBase = useMemo(
    () => filterByInitial(filterByGroup(students), searchBy, selectedLetter),
    [filterByGroup, students, searchBy, selectedLetter]
  );
  const counts = useMemo(() => getCountsByStatus(countsBase), [countsBase]);

  // Resets year/class and name-initials; keeps order status. POS-3094 / POS-3132.
  const clearFilters = () => {
    resetGroupFilter();
    collectActions.setSearchBy("firstName");
  };

  const hasStudents = status === "success" && students.length > 0;
  const hasActiveFilters =
    hasGroupSelection || selectedLetter !== null || searchBy !== "firstName";

  const view = getDiscoveryView({
    status,
    hasStudents,
    hasActiveFilters,
    hasMatches: filteredStudents.length > 0
  });

  return (
    <PaginationContextProvider
      // Reset to the first page when any filter changes (no subtree remount).
      resetKey={`${statusFilter}:${groupSelectionKey}:${searchBy}:${
        selectedLetter ?? "all"
      }`}
      maxItemsPerPage={studentsPerPage}
      items={filteredStudents}
    >
      <Layout
        header={
          <DiscoveryHeader
            view={view}
            statusFilter={statusFilter}
            counts={counts}
            onStatusChange={collectActions.setStatusFilter}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />
        }
        sidebar={
          // Group filter (own island) scrolls above; name keyboard stays pinned below.
          <div className="flex h-full flex-col gap-4">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <GroupFilterRegion
                status={schoolStatus}
                isActive={hasGroupFilter}
                layers={layers}
                onRetry={refetchSchool}
                isRetrying={isRefetchingSchool}
              />
            </div>
            <CollectNameFilter
              className="shrink-0"
              searchBy={searchBy}
              onSearchByChange={collectActions.setSearchBy}
              selectedLetter={selectedLetter}
              onSelectLetter={collectActions.setSelectedLetter}
              alphabet={alphabet}
              availableLetters={availableLetters}
            />
          </div>
        }
        footer={
          <Footer>{view === "grid" ? <FooterPagination /> : null}</Footer>
        }
      >
        <DiscoveryMain
          view={view}
          onRetry={refetch}
          isRetrying={isRefetching}
          onClearFilters={clearFilters}
          rows={rows}
          columns={GRID_COLUMNS}
        />
      </Layout>
    </PaginationContextProvider>
  );
};
